import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'

// Use
import useMobile from './mobile'

// Helpers
import { isRowChecked, resolveSortBy, resolveSortDirection } from './helpers'

// Components
import BaseTable from 'antd/lib/table'
import Checkbox from 'antd/lib/checkbox'
import Loading from 'ui/Loading'
import DatatablePagination from './DatatablePagination'

// Assets
import LaptopIcon from 'assets/images/laptop.png'

// Styles
import './Datatable.scss'

export const Datatable = ({
  className,
  loading,
  children,
  data,
  columns,
  total,
  page,
  perPage,
  selectable,
  selected,
  selectMode,
  responsive,
  rowHighlight,
  onPageChange,
  onSortChange,
  onSelectModeChange,
  onSelectedChange,
  ...props
}) => {
  const isMobile = useMobile()

  const showHeader = isMobile && responsive ? false : true

  const onChange = (paging, filters, { column, field, order }) => {
    onSortChange(
      resolveSortBy(column, field),
      resolveSortDirection(column, order)
    )
  }

  const onCheckAllChange = useCallback(
    (isChecked) => {
      onSelectModeChange(isChecked ? 'all' : null)
      onSelectedChange([])
    },
    [onSelectModeChange, onSelectedChange]
  )

  const onRowSelect = useCallback(
    (row, isSelect) => {
      let selectList = []
      let selectType = selectMode

      if (selectType !== 'all') {
        if (!selectMode) {
          // Case there is no select record then select one record
          selectType = 'only'
          selectList = []
        } else if (selectType === 'only' || selectType === 'exclude') {
          // Clone selected list
          selectList = [...selected]
        }
      } else {
        // Case: select all then uncheck one record
        selectType = 'exclude'
        selectList = []
      }

      if (
        (selectType === 'exclude' && isSelect) ||
        (selectType !== 'exclude' && !isSelect)
      ) {
        const index = selectList.findIndex((x) => x.id === row.id)

        if (index >= 0) {
          // Unselected
          selectList.splice(index, 1)
        }
      } else {
        selectList.push(row)
      }

      // Recheck selected type
      if (selectType === 'exclude') {
        if (selectList.length === 0 && isSelect) {
          selectType = 'all'
        }

        if (selectList.length === total) {
          selectType = null
          selectList = []
        }
      } else {
        if (selectList.length === 0) {
          selectType = null
        }

        if (selectList.length === total) {
          selectType = 'all'
          selectList = []
        }
      }

      onSelectModeChange(selectType)
      onSelectedChange(selectList)
    },
    [onSelectedChange, onSelectModeChange, selected, selectMode, total]
  )

  // Table columns
  const tableColumns = useMemo(() => {
    const items = columns
      .filter((x) => {
        if (isMobile && responsive) {
          return !!x.renderMobile
        }

        return true
      })
      .map((x) => {
        const c = []

        if (x.onCell) {
          c.push('cursor-pointer')
        }

        if (x.noWrap) {
          c.push('no-wrap')
        }

        return {
          ...x,
          className: c.join(' '),
          render: isMobile && responsive ? x.renderMobile : x.render,
        }
      })

    if (selectable) {
      const checkbox = {
        title: (
          <Checkbox
            checked={selectMode === 'all'}
            onChange={(event) => onCheckAllChange(event.target.checked)}
          />
        ),
        dataIndex: 'select-checkbox',
        width: '40px',
        className: 'td-checkbox',
        render(text, row) {
          return (
            <div className="x-datatable-checkbox">
              <Checkbox
                checked={isRowChecked({ row, selectMode, selected })}
                onChange={(event) => onRowSelect(row, event.target.checked)}
              />
            </div>
          )
        },
      }

      // Push / Unshift checkbox
      isMobile && responsive ? items.push(checkbox) : items.unshift(checkbox)
    }

    return items
  }, [
    columns,
    selectable,
    isMobile,
    responsive,
    selectMode,
    onCheckAllChange,
    selected,
    onRowSelect,
  ])

  // Row class name
  const rowClassName = useCallback(
    (record, index) => {
      const list = []

      if (rowHighlight === true) {
        list.push('datatable-row-highlight')
      }

      if (typeof rowHighlight === 'function' && rowHighlight(record, index)) {
        list.push('datatable-row-highlight')
      }

      return list.join(' ')
    },
    [rowHighlight]
  )

  // Table class name
  const tableClassName = useMemo(() => {
    const classList = ['datatable', className]
    if (responsive && isMobile) {
      classList.push('datatable-mobile')
    }
    return classList.join(' ')
  }, [className, isMobile, responsive])

  // Table no support responsive
  if (isMobile && responsive === false) {
    return (
      <div className="hidden-notify">
        <img src={LaptopIcon} alt="laptop" width="60px" />
        <p className="message">
          Open page on a DESKTOP to <br />
          view the table
        </p>
      </div>
    )
  }

  return (
    <Loading spinning={loading}>
      {children}
      <BaseTable
        className={tableClassName}
        rowKey={'id'}
        dataSource={data}
        columns={tableColumns}
        pagination={false}
        showSorterTooltip={false}
        scroll={{ x: '100%' }}
        rowClassName={rowClassName}
        showHeader={showHeader}
        onChange={onChange}
        {...props}
      />
      {total > 0 && (
        <DatatablePagination
          page={page}
          total={total}
          perPage={perPage}
          data={data}
          onPageChange={(e) => onPageChange(e)}
        />
      )}
    </Loading>
  )
}

Datatable.propTypes = {
  className: PropTypes.string,
  loading: PropTypes.bool,
  columns: PropTypes.array,
  data: PropTypes.any,
  perPage: PropTypes.number,
  page: PropTypes.number,
  total: PropTypes.number,
  selectable: PropTypes.bool,
  selected: PropTypes.any,
  selectMode: PropTypes.any,
  children: PropTypes.node,
  responsive: PropTypes.bool,
  rowHighlight: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  onSortChange: PropTypes.any,
  onPageChange: PropTypes.any,
  onSelectModeChange: PropTypes.any,
  onSelectedChange: PropTypes.any,
}

Datatable.defaultProps = {
  className: '',
  loading: false,
  columns: [],
  data: [],
  perPage: 20,
  page: null,
  total: null,
  selectable: false,
  selected: null,
  selectMode: null,
  children: null,
  responsive: false,
  rowHighlight: false,
  onSortChange: () => {},
  onPageChange: () => {},
  onSelectModeChange: () => {},
  onSelectedChange: () => {},
}

export default Datatable
